import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProgrammerContext } from '@/hid/ProgrammerContext';

const ConfigBytesView: React.FC = () => {
  const { t } = useTranslation('webflash');
  const { programmer, selectedChip, status } = useProgrammerContext();
  const [showWriteWarning, setShowWriteWarning] = useState(false);

  const isReady = status === 'connected' && programmer && selectedChip;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t('config.title')}
      </h3>

      {!selectedChip ? (
        <p className="text-sm text-gray-400">{t('config.noChip')}</p>
      ) : selectedChip.configSize === 0 ? (
        <p className="text-sm text-gray-400">{t('config.notAvailable')}</p>
      ) : (
        <div className="space-y-4">
          {/* Config byte table (placeholder) */}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <th className="pb-2">{t('config.field')}</th>
                <th className="pb-2">{t('config.value')}</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr>
                <td className="py-1">{t('config.lock')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
              <tr>
                <td className="py-1">{t('config.bootSelect')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
              <tr>
                <td className="py-1">{t('config.ldromSize')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
              <tr>
                <td className="py-1">{t('config.brownOutReset')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
              <tr>
                <td className="py-1">{t('config.watchdog')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
              <tr>
                <td className="py-1">{t('config.raw')}</td>
                <td className="py-1 font-mono">--</td>
              </tr>
            </tbody>
          </table>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              disabled={!isReady}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {t('config.readConfig')}
            </button>
            <button
              disabled={!isReady}
              onClick={() => setShowWriteWarning(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {t('config.writeConfig')}
            </button>
          </div>

          {/* Destructive write warning dialog */}
          {showWriteWarning && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
                <h4 className="text-lg font-semibold text-red-600 mb-2">{t('config.writeWarningTitle')}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('config.writeWarningMessage')}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowWriteWarning(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => setShowWriteWarning(false)}
                    disabled={!isReady}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {t('config.confirmWrite')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigBytesView;
